import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FileText, Lock, Download, Loader2, ShieldCheck } from "lucide-react";
import { api, API, formatApiErrorDetail } from "../../lib/api";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-[#E2E8F0] bg-white text-[#0B1B2B] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors";

export const ResourceVault = () => {
  const [resources, setResources] = useState([]);
  const [form, setForm] = useState({ name: "", organization: "", email: "" });
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/resources").then(({ data }) => setResources(data.resources)).catch(() => {});
  }, []);

  const unlock = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/resources/access", form);
      setToken(data.access_token);
      setResources(data.resources);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="resources" className="py-20 md:py-32 bg-[#F8FAFC]" data-testid="resource-vault-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Partner Resource Vault</p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-[#0B1B2B] tracking-tight leading-tight">
              Institutional Intelligence, Gated for Partners.
            </h2>
            <p className="mt-5 text-[#64748B] leading-relaxed text-sm md:text-base">
              Executive-series whitepapers covering regulatory mechanics, accreditation evidence architecture, and
              FQHC revenue alignment — released to verified institutional contacts.
            </p>
            {!token ? (
              <form onSubmit={unlock} className="mt-8 space-y-3 p-6 bg-white border border-[#E2E8F0] rounded-xl shadow-sm" data-testid="vault-gate-form">
                <div className="flex items-center gap-2 text-xs font-semibold text-[#0B1B2B] uppercase tracking-wide">
                  <Lock size={13} className="text-[#D4AF37]" /> Verify to Unlock
                </div>
                <input className={inputCls} placeholder="Full Name" required value={form.name} onChange={set("name")} data-testid="vault-name-input" />
                <input className={inputCls} placeholder="Organization" required value={form.organization} onChange={set("organization")} data-testid="vault-org-input" />
                <input className={inputCls} type="email" placeholder="Work Email" required pattern="[^@\s]+@[^@\s]+\.[^@\s]{2,}" title="Enter a valid institutional email address" value={form.email} onChange={set("email")} data-testid="vault-email-input" />
                {error && <p className="text-sm text-red-600" data-testid="vault-error">{error}</p>}
                <button type="submit" disabled={busy} className="w-full bg-[#0B1B2B] text-white font-semibold py-3 rounded-xl hover:bg-[#152a40] transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-testid="vault-unlock-button">
                  {busy && <Loader2 size={15} className="animate-spin" />}
                  Unlock Resource Vault
                </button>
                <p className="text-[11px] text-[#94A3B8] leading-relaxed">
                  Access is granted instantly. Your details are handled under our institutional privacy standards.
                </p>
              </form>
            ) : (
              <div className="mt-8 p-6 bg-[#0B1B2B] rounded-xl flex items-start gap-3" data-testid="vault-unlocked-banner">
                <ShieldCheck size={20} className="text-[#D4AF37] mt-0.5 shrink-0" />
                <div>
                  <div className="text-white font-semibold text-sm">Vault Unlocked</div>
                  <p className="text-slate-400 text-xs mt-1 leading-relaxed">
                    All executive-series documents are now available for download. Access remains active for 24 hours.
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="lg:col-span-8 mt-10 lg:mt-0 grid grid-cols-1 md:grid-cols-3 gap-5">
            {resources.map((r, idx) => (
              <motion.div
                key={r.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="flex flex-col bg-white border border-[#E2E8F0] rounded-xl p-6 hover:border-[#D4AF37]/60 hover:shadow-lg transition-all duration-300"
                data-testid={`vault-resource-${r.slug}`}
              >
                <div className="flex items-center justify-between">
                  <FileText size={22} className="text-[#D4AF37]" />
                  {!token && <Lock size={14} className="text-[#94A3B8]" data-testid={`vault-lock-${r.slug}`} />}
                </div>
                <h3 className="mt-4 font-serif text-lg text-[#0B1B2B] font-semibold leading-snug">{r.title}</h3>
                <p className="mt-1.5 text-xs text-[#D4AF37] font-medium leading-snug">{r.subtitle}</p>
                <p className="mt-3 text-sm text-[#64748B] leading-relaxed flex-1">{r.description}</p>
                {token ? (
                  <a
                    href={`${API}/resources/${r.slug}/download?token=${token}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-5 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0B1B2B] text-sm font-semibold py-2.5 rounded-xl hover:bg-[#e8c968] transition-colors"
                    data-testid={`vault-download-${r.slug}`}
                  >
                    <Download size={15} /> Download PDF
                  </a>
                ) : (
                  <div className="mt-5 inline-flex items-center justify-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] text-[#94A3B8] text-sm font-medium py-2.5 rounded-xl" data-testid={`vault-locked-${r.slug}`}>
                    <Lock size={13} /> Gated — Verify to Access
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
